import { useState } from 'react'
import { fmtTime } from '@/shared/lib/format'
import { cls } from '@/ui/cls'
import { Ico } from '@/ui/Ico'
import { useOpenedToday } from '@/shared/api/session'
import { SkelRows } from '@/shared/Skeletons'
import { stageLabel } from '@/shared/pipelineColors'
import { locationLabel } from '@/shared/regions'
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '@/components/ui/popover'

// On-demand "companies I opened today" list (RADAR-11). Ebba uses it to trace
// back a lead she clicked earlier (e.g. "whose number was that?"). Not a
// default view — a topbar dropdown that fetches only when opened.
//
// On Radix Popover: outside click, Escape and focus return come from the
// primitive. `open` stays in React state (not just Radix's own) because
// useOpenedToday gates its fetch on it — the list loads only once opened.
export default function OpenedTodayMenu({ onOpenLead }: { onOpenLead: (id: string) => void }) {
  const [open, setOpen] = useState(false)
  const { data, isLoading } = useOpenedToday(open)
  const leads = data?.leads || []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Företag du öppnat idag"
          aria-pressed={open}
          className={cls(
            'inline-flex items-center gap-1.5 h-[30px] px-2.5 border cursor-pointer whitespace-nowrap',
            'text-xs font-medium rounded-[2px] transition-colors [&_svg]:w-3.5 [&_svg]:h-3.5',
            open
              ? 'bg-ink-1000 border-ink-1000 text-white'
              : 'bg-transparent border-ink-200 text-ink-700 hover:text-ink-1000 hover:border-ink-1000',
          )}
        >
          {Ico.clock}
          <span className="max-[900px]:hidden">Idag</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={4} className="w-80 max-h-[70vh] overflow-auto rounded-[2px] p-0">
        <PopoverTitle className="px-3 py-2 border-b border-ink-100 text-[10px] uppercase tracking-widest text-ink-500 sticky top-0 bg-white font-normal">
          Öppnade idag{leads.length ? ` · ${leads.length}` : ''}
        </PopoverTitle>
        {isLoading && <SkelRows rows={3} label="Laddar öppnade företag" className="px-3 py-4" />}
        {!isLoading && leads.length === 0 && (
          <div className="px-3 py-4 text-[13px] text-ink-500">Inga företag öppnade idag än.</div>
        )}
        {leads.map(l => (
          <button
            key={l.id}
            type="button"
            onClick={() => { onOpenLead(l.id); setOpen(false) }}
            className="w-full text-left px-3 py-2 border-b border-ink-50 hover:bg-ink-50 cursor-pointer flex items-baseline justify-between gap-2"
          >
            <span className="min-w-0">
              <span className="block text-[13px] text-ink-1000 truncate">{l.company}</span>
              <span className="block text-[11px] text-ink-500 truncate">
                {[locationLabel(l.location), l.stage && stageLabel(l.stage)].filter(Boolean).join(' · ')}
              </span>
            </span>
            <span className="text-[11px] text-ink-500 tabular-nums shrink-0">
              {l.openedAt ? fmtTime(l.openedAt) : ''}
            </span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}
